import { useState } from "react";
import { Card, Switch } from "antd";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line } from "recharts";
import { ValueType, NameType } from "recharts/types/component/DefaultTooltipContent";
import { Payload } from "recharts/types/component/DefaultTooltipContent";

type MonthlyDTI = {
  year: number;
  month: number;
  debt_payments: number;
  dti_percentage: number;
};

type QuarterlyDTI = {
  year: number;
  quarter: number;
  income: number;
  debt_payments: number;
  dti_percentage: number;
};

type ChartDataItem = {
  label: string;
  year: number;
  month?: number;
  quarter?: number;
  debt_payments: number;
  dti_percentage: number;
};

type DebtToIncomeProps = {
  data: {
    monthly_dti: MonthlyDTI[];
    quarterly_dti: QuarterlyDTI[];
    total_outstanding_debt: number;
    monthly_debt_obligations: number;
    average_monthly_dti: number;
    debt_breakdown: unknown;
  };
};

const DebtToIncomeChart = ({ data }: DebtToIncomeProps) => {
  const [view, setView] = useState<"monthly" | "quarterly">("monthly");

  const chartData: ChartDataItem[] =
    view === "monthly"
      ? data.monthly_dti.map((item) => ({
          ...item,
          label: `${item.year}-${item.month}`,
        }))
      : data.quarterly_dti.map((item) => ({
          ...item,
          label: `${item.year} Q${item.quarter}`,
        }));

  if (!data) return null;
  return (
    <Card
      title="Debt-to-Income Ratio Trend"
      extra={
        <div className="flex gap-2">
          <Switch
            checked={view === "quarterly"}
            checkedChildren="Quarterly"
            unCheckedChildren="Monthly"
            onChange={(checked) => setView(checked ? "quarterly" : "monthly")}
            className="!bg-blue-500 hover:!bg-blue-600"
          />
        </div>
      }
      className=" border border-gray-300"
    >
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fill: "#4b5563", fontSize: 12 }} />
            <YAxis tick={{ fill: "#4b5563", fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
              }}
              formatter={(value: ValueType, _name: NameType, item: Payload<ValueType, NameType>) => {
                const payload = item.payload as ChartDataItem;
                return [`${Number(payload.dti_percentage).toFixed(1)}%`, "DTI Ratio"];
              }}
              // labelFormatter={(label: string, payload) => {
              //   const item = payload[0]?.payload as ChartDataItem;
              //   return view === "monthly"
              //     ? `Year: ${item.year}, Month: ${item.month}, Debt: $${item.debt_payments.toLocaleString()}`
              //     : `Year: ${item.year}, Q${item.quarter}, Debt: $${item.debt_payments.toLocaleString()}`;
              // }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey="dti_percentage"
              name="DTI Ratio"
              stroke="#ef4444"
              strokeWidth={3}
              dot={{ fill: "#dc2626", r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default DebtToIncomeChart;
